/**
 * Utilitaires pour gérer les JWT tokens
 */

interface JWTPayload {
  exp: number
  iat: number
  username?: string
  roles?: string[]
}

/**
 * Décode un JWT token sans vérifier la signature
 * @param token Le token JWT à décoder
 * @returns Le payload décodé ou null si invalide
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const payload = parts[1]
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodedPayload)
  } catch (error) {
    console.error('Erreur lors du décodage du JWT:', error)
    return null
  }
}

/**
 * Vérifie si un token JWT a expiré
 * @param token Le token JWT à vérifier
 * @returns true si le token a expiré, false sinon
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return true
  }

  // Vérifier si le token a expiré (en secondes)
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime
}

/**
 * Vérifie si un token JWT va expirer bientôt
 * @param token Le token JWT à vérifier
 * @param bufferSeconds Nombre de secondes avant l'expiration (par défaut 5 minutes)
 * @returns true si le token va expirer dans les prochaines minutes
 */
export function willTokenExpireSoon(token: string, bufferSeconds: number = 300): boolean {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return true
  }

  // Vérifier si le token va expirer dans les prochaines minutes
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime + bufferSeconds
}

/**
 * Obtient le temps restant avant expiration du token en secondes
 * @param token Le token JWT
 * @returns Le nombre de secondes avant expiration, ou 0 si expiré/invalide
 */
export function getTokenTimeToExpiry(token: string): number {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return 0
  }

  const currentTime = Math.floor(Date.now() / 1000)
  const timeRemaining = payload.exp - currentTime
  return Math.max(0, timeRemaining)
}

